// frontend/src/components/FilterForm/DesktopFilterForm.js
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import CategoriesFilter from './CategoriesFilter';
import LocationTimeComboPicker from './LocationTimeComboPicker';
import FiltersModal from './FiltersModal';
import { useTranslation } from 'react-i18next';
import { getMunicipality } from '../../utils/GetMunicipality';
import useFilterFormLogic from '../../hooks/useFilterFormLogic';
import MoreFilterIcon from '../Icons/MoreFilterIcon';
import Button from '../Button/Button';
import { useEventCategories } from '../../hooks/useEventCategories';

const DesktopFilterForm = ({ filters, setFilters, municipality, setMunicipality, showFiltersModal, setShowFiltersModal, ...props }) => {
  const { t: tCategories } = useTranslation('eventCategories');
  const { t: tFilter } = useTranslation('filterForm');
  const { eventTypes } = useEventCategories();

  // Claves de categorías a usar
  const categoryKeys = eventTypes.map(type => type.key);
  const minRadius = 10;
  const maxRadius = 1000;

  const [address, setAddress] = useState(filters.location?.address || '');
  const [showLocationFields, setShowLocationFields] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tempAddress, setTempAddress] = useState(filters.location?.address || '');
  const [tempRadius, setTempRadius] = useState(filters.radius || maxRadius);

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const locationRef = useRef(null);
  const dropdownRef = useRef(null);

  // Uso del hook para la lógica de filtros "otros"
  const { tempOtherFilters, handleOtherFiltersChange, handleResetOtherFilters } = useFilterFormLogic(filters);

  // Función para manejo directo de filtros (por ej. desde el CategoriesFilter)
  const handleDirectFilterChange = (type, value) => {
    if (type === 'typology') {
      const updated = filters.typology.includes(value)
        ? filters.typology.filter(item => item !== value)
        : [...filters.typology, value];
      setFilters(prev => ({ ...prev, typology: updated }));
    } else {
      const current = Array.isArray(filters[type]) ? [...filters[type]] : filters[type] ? [filters[type]] : [];
      const updated = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      setFilters(prev => ({ ...prev, [type]: updated }));
    }
  };

  const handleCancelOtherFilters = (e) => {
    e && e.preventDefault();
    setShowModal(false);
  };

  const handleApplyOtherFilters = () => {
    setFilters(prev => ({
      ...prev,
      typology: [...tempOtherFilters.typology],
      terrain: [...tempOtherFilters.terrain],
      experience: [...tempOtherFilters.experience],
      ticketType: [...tempOtherFilters.ticketType],
      needsVehicle: [...tempOtherFilters.needsVehicle],
      vehicleTypes: [...tempOtherFilters.vehicleTypes]
    }));
    setShowModal(false);
  };

  const handleCloseOtherFiltersModal = () => {
    setShowModal(false);
  };

  const handleResetFilters = () => {
    handleResetOtherFilters();
  };

  // No duplicamos la actualización de tempOtherFilters; el hook se encarga de ello.
  const toggleLocationFields = () => {
    if (!showLocationFields) {
      setTempAddress(address || '');
      setTempRadius(filters.radius);
    }
    setShowLocationFields(!showLocationFields);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        locationRef.current &&
        !locationRef.current.contains(event.target) &&
        !event.target.closest('#applyButton') &&
        !event.target.closest('#cancelButton')
      ) {
        setShowLocationFields(false);
      }
    };

    if (showLocationFields) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showLocationFields]);

  useEffect(() => {
    if (filters.location?.address) {
      setAddress(filters.location.address);
      setTempAddress(filters.location.address);
    }
  }, [filters.location]);

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.geometry) {
      const selectedAddress = place.formatted_address || '';
      setTempAddress(selectedAddress);
      setAddress(selectedAddress);
      const userMunicipality = getMunicipality(place.address_components || []);
      console.log('Municipio seleccionado:', userMunicipality);
    }
  };

  const handleFilterChange = (type, value) => {
    if (type === 'radius') {
      const numericValue = Number(value);
      if (isNaN(numericValue)) return;
      const radius = Math.min(Math.max(numericValue, minRadius), maxRadius);
      setFilters(prev => ({ ...prev, [type]: radius }));
    } else if (type === 'typology') {
      const updated = updateTypologyFilter(filters.typology, value);
      setFilters(prev => ({ ...prev, typology: updated }));
    } else {
      setFilters(prev => ({ ...prev, [type]: value }));
    }
  };


  // const updateTypologyFilter = (currentTypology, value) => {
  //   return currentTypology.includes(value)
  //     ? currentTypology.filter(item => item !== value)
  //     : [...currentTypology, value];
  // };

  const updateTypologyFilter = (current, value) =>
    current.includes(value) ? current.filter(item => item !== value) : [...current, value];


  // const handleFilterChange = (type, value) => {
  //   if (type === 'radius') {
  //     const numericValue = Number(value);
  //     if (isNaN(numericValue)) return;
  //     const radius = Math.min(Math.max(numericValue, minRadius), maxRadius);
  //     setFilters(prev => ({ ...prev, [type]: radius }));
  //   } else if (type === 'typology') {
  //     const updatedTypology = updateTypologyFilter(filters.typology, value);
  //     setFilters(prev => ({ ...prev, typology: updatedTypology }));
  //   } else {
  //     setFilters(prev => ({ ...prev, [type]: value }));
  //   }
  // };

  const handleApply = (e) => {
    e.preventDefault();
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        const userLocation = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          address: tempAddress,
        };
        const userMunicipality = getMunicipality(place.address_components || []);
        const validatedRadius = Math.min(Math.max(tempRadius, minRadius), maxRadius);
        setFilters(prev => ({
          ...prev,
          location: userLocation,
          radius: validatedRadius,
        }));
        setMunicipality(userMunicipality);
        localStorage.setItem('location', JSON.stringify(userLocation));
        localStorage.setItem('municipality', userMunicipality);
        setAddress(tempAddress);
      } else {
        const validatedRadius = Math.min(Math.max(tempRadius, minRadius), maxRadius);
        setFilters(prev => ({ ...prev, radius: validatedRadius }));
      }
    } else {
      const validatedRadius = Math.min(Math.max(tempRadius, minRadius), maxRadius);
      setFilters(prev => ({ ...prev, radius: validatedRadius }));
    }
    setShowLocationFields(false);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setTempAddress(address);
    setTempRadius(filters.radius);
    setShowLocationFields(false);
  };


  // // Función auxiliar para transformar el valor "all" en el array completo
  // const transformFilter = (filterName, values) => {
  //   const allValuesMap = {
  //     terrain: ['road', 'offroad'],
  //     experience: ['beginner', 'intermediate', 'advanced'],
  //     ticketType: ['free', 'paid'],
  //     needsVehicle: ['true', 'false']
  //   };
  //   if (Array.isArray(values) && values.includes('all')) {
  //     return allValuesMap[filterName] || values;
  //   }
  //   return values;
  // };

  // const handleApplyOtherFilters = () => {
  //   setFilters(prev => ({
  //     ...prev,
  //     typology: [...tempOtherFilters.typology],
  //     terrain: transformFilter('terrain', tempOtherFilters.terrain),
  //     experience: transformFilter('experience', tempOtherFilters.experience),
  //     ticketType: transformFilter('ticketType', tempOtherFilters.ticketType),
  //     needsVehicle: transformFilter('needsVehicle', tempOtherFilters.needsVehicle)
  //   }));
  //   setShowModal(false);
  // };

  // const handleCancelOtherFilters = (e) => {
  //   e.preventDefault();
  //   setShowModal(false);
  // };

  // const handleCloseOtherFiltersModal = () => {
  //   setShowModal(false);
  // };

  // const handleResetFilters = () => {
  //   handleResetOtherFilters();
  // };

  // const handleDirectFilterChange = (type, value) => {
  //   if (type === 'typology') {
  //     const updatedTypology = filters.typology.includes(value)
  //       ? filters.typology.filter(item => item !== value)
  //       : [...filters.typology, value];
  //     setFilters(prev => ({ ...prev, typology: updatedTypology }));
  //   } else {
  //     const currentValues = Array.isArray(filters[type])
  //       ? [...filters[type]]
  //       : filters[type] ? [filters[type]] : [];
  //     const updatedValues = currentValues.includes(value)
  //       ? currentValues.filter(item => item !== value)
  //       : [...currentValues, value];
  //     setFilters(prev => ({ ...prev, [type]: updatedValues }));
  //   }
  // };

  // const handleApply = (e) => {
  //   e.preventDefault();
  //   if (autocompleteRef.current) {
  //     const place = autocompleteRef.current.getPlace();
  //     if (place && place.geometry) {
  //       const userLocation = {
  //         lat: place.geometry.location.lat(),
  //         lng: place.geometry.location.lng(),
  //         address: tempAddress,
  //       };
  //       const userMunicipality = getMunicipality(place.address_components || []);
  //       const validatedRadius = Math.min(Math.max(tempRadius, minRadius), maxRadius);
  //       setFilters(prev => ({
  //         ...prev,
  //         location: userLocation,
  //         radius: validatedRadius,
  //       }));
  //       setMunicipality(userMunicipality);
  //       localStorage.setItem('location', JSON.stringify(userLocation));
  //       localStorage.setItem('municipality', userMunicipality);
  //       setAddress(tempAddress);
  //     } else {
  //       const validatedRadius = Math.min(Math.max(tempRadius, minRadius), maxRadius);
  //       setFilters(prev => ({ ...prev, radius: validatedRadius }));
  //     }
  //   } else {
  //     const validatedRadius = Math.min(Math.max(tempRadius, minRadius), maxRadius);
  //     setFilters(prev => ({ ...prev, radius: validatedRadius }));
  //   }
  //   setShowLocationFields(false);
  // };

  // const handleCancel = (e) => {
  //   e.preventDefault();
  //   setTempAddress(address);
  //   setTempRadius(filters.radius);
  //   setShowLocationFields(false);
  // };

  // Funciones para aplicar, cancelar y cerrar el modal de filtros "otros"
  // const handleApplyOtherFilters = () => {
  //   setFilters(prev => ({
  //     ...prev,
  //     typology: [...tempOtherFilters.typology],
  //     terrain: Array.isArray(tempOtherFilters.terrain) ? [...tempOtherFilters.terrain] : [],
  //     experience: Array.isArray(tempOtherFilters.experience) ? [...tempOtherFilters.experience] : [],
  //     ticketType: Array.isArray(tempOtherFilters.ticketType) ? [...tempOtherFilters.ticketType] : [],
  //     needsVehicle: Array.isArray(tempOtherFilters.needsVehicle) ? [...tempOtherFilters.needsVehicle] : []
  //   }));
  //   setShowModal(false);
  // };

  // const handleApplyOtherFilters = () => {
  //   setFilters(prev => ({
  //     ...prev,
  //     typology: [...tempOtherFilters.typology],
  //     terrain: Array.isArray(tempOtherFilters.terrain) ? tempOtherFilters.terrain.flat() : [],
  //     experience: Array.isArray(tempOtherFilters.experience) ? tempOtherFilters.experience.flat() : [],
  //     ticketType: Array.isArray(tempOtherFilters.ticketType) ? tempOtherFilters.ticketType.flat() : [],
  //     needsVehicle: Array.isArray(tempOtherFilters.needsVehicle) ? tempOtherFilters.needsVehicle.flat() : []
  //   }));
  //   setShowModal(false);
  // };

  // const handleCancelOtherFilters = (e) => {
  //   e.preventDefault();
  //   // Reinicia tempOtherFilters a los valores actuales de filters
  //   setShowModal(false);
  // };

  // const handleCloseOtherFiltersModal = () => {
  //   // Reinicia tempOtherFilters a los valores actuales de filters
  //   setShowModal(false);
  // };

  // const handleResetFilters = () => {
  //   handleResetOtherFilters();
  // };

  // // Cambios directos en la vista principal
  // const handleDirectFilterChange = (type, value) => {
  //   if (type === 'typology') {
  //     const updatedTypology = filters.typology.includes(value)
  //       ? filters.typology.filter(item => item !== value)
  //       : [...filters.typology, value];
  //     setFilters(prev => ({ ...prev, typology: updatedTypology }));
  //   } else {
  //     const currentValues = Array.isArray(filters[type])
  //       ? [...filters[type]]
  //       : filters[type] ? [filters[type]] : [];
  //     const updatedValues = currentValues.includes(value)
  //       ? currentValues.filter(item => item !== value)
  //       : [...currentValues, value];
  //     setFilters(prev => ({ ...prev, [type]: updatedValues }));
  //   }
  // };

  return (
    <Filter>
      <FormContainer>
        <MainFilters>
          <LocationTimeComboPicker
            toggleLocationFields={toggleLocationFields}
            locationRef={locationRef}
            municipality={municipality}
            filters={filters}
            handleFilterChange={handleFilterChange}
            tFilter={tFilter}
            showLocationFields={showLocationFields}
            tempAddress={tempAddress}
            setTempAddress={setTempAddress}
            tempRadius={tempRadius}
            setTempRadius={setTempRadius}
            handleApply={handleApply}
            handleCancel={handleCancel}
            autocompleteRef={autocompleteRef}
            inputRef={inputRef}
            handlePlaceSelect={handlePlaceSelect}
            minRadius={minRadius}
            maxRadius={maxRadius}
            dropdownRef={dropdownRef}
          />

          <Button $variant="outline" type="button" onClick={() => setShowModal(true)} size="l">
            {tFilter('modalOtherFilters.header')}
            <MoreFilterIcon stroke={theme.colors.defaultStrong} />
          </Button>
        </MainFilters>

        <SecondaryFilters>
          <CategoriesFilter
            categoryKeys={categoryKeys}
            filters={filters}
            handleDirectFilterChange={handleDirectFilterChange}
            tCategories={tCategories}
          />
        </SecondaryFilters>
      </FormContainer>

      {showModal && (
        <FiltersModal
          tempOtherFilters={tempOtherFilters}
          handleOtherFiltersChange={handleOtherFiltersChange}
          handleCancelOtherFilters={handleCancelOtherFilters}
          handleApplyOtherFilters={handleApplyOtherFilters}
          handleCloseOtherFiltersModal={handleCloseOtherFiltersModal}
          handleResetFilters={handleResetFilters}
          categoryKeys={categoryKeys}
          tFilter={tFilter}
          tCategories={tCategories}
        />
      )}
    </Filter>
  );
};

export default DesktopFilterForm;

//////////////////////////////////////////////
// ESTILOS (se muestran solo los bloques esenciales)
//////////////////////////////////////////////

const Filter = styled.section`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  flex-direction: column;
  z-index: 10; 
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  max-width: 1400px;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  padding-bottom: 0;
  width: 100%;
`;

const MainFilters = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
`;

const SecondaryFilters = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
`;
